/**
 * Minimal SSE helper using fetch + ReadableStream so we can attach the
 * session Authorization header (EventSource doesn't support custom
 * headers, and we don't want to put the bearer token in a URL query
 * string).
 *
 * Usage:
 *   const stop = streamSse<JobStatus>('/v1/jobs/abc/stream', {
 *     onEvent: (evt) => { if (evt.type === 'status') setJob(evt.data) },
 *     onClose: () => { ... fall back to poll ... },
 *   })
 *   // later: stop()
 *
 * Error handling is intentionally permissive — callers are expected to
 * have a polling fallback (e.g. a useQuery with refetchInterval) so a
 * proxy dropping the connection just degrades gracefully.
 */

export type SseEvent<T = unknown> = {
  type: string
  data: T | null
  raw: string
}

export interface StreamSseOptions<T = any> {
  /** Session token to send as `Authorization: Bearer …`. Pulled from
   *  localStorage by default so callers don't need to plumb it. */
  token?: string | null
  onEvent?: (evt: SseEvent<T>) => void
  /** Called when the stream terminates for any reason (server close,
   *  network error, manual abort). */
  onClose?: (info: { reason: 'server' | 'error' | 'abort'; error?: unknown }) => void
  /** Overrides the API base URL. Defaults to VITE_API_BASE_URL. */
  apiBase?: string
}

export function streamSse<T = any>(path: string, opts: StreamSseOptions<T> = {}): () => void {
  const apiBase = opts.apiBase ?? ((import.meta as any).env?.VITE_API_BASE_URL || '')
  const token = opts.token ?? localStorage.getItem('cenva_intel_session_token')
  const controller = new AbortController()

  let aborted = false
  const reportClose = (reason: 'server' | 'error' | 'abort', error?: unknown) => {
    if (opts.onClose) {
      try { opts.onClose({ reason, error }) } catch { /* swallow */ }
    }
  }

  ;(async () => {
    try {
      const res = await fetch(`${apiBase}${path}`, {
        method: 'GET',
        // Include cookies so the HttpOnly session cookie authenticates
        // the stream, matching the primary auth path used by apiFetch.
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      })
      if (!res.ok || !res.body) {
        reportClose('error', new Error(`SSE ${res.status} ${res.statusText}`))
        return
      }

      const decoder = new TextDecoder()
      const reader = res.body.getReader()
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          reportClose('server')
          return
        }
        buffer += decoder.decode(value, { stream: true })
        // SSE frames are delimited by a blank line (\n\n).
        let idx: number
        // eslint-disable-next-line no-cond-assign
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx)
          buffer = buffer.slice(idx + 2)
          const parsed = parseFrame<T>(frame)
          if (parsed && opts.onEvent) {
            try { opts.onEvent(parsed) } catch { /* swallow user error */ }
          }
        }
      }
    } catch (err: any) {
      if (aborted || err?.name === 'AbortError') return
      reportClose('error', err)
    }
  })()

  return () => {
    if (aborted) return
    aborted = true
    controller.abort()
    reportClose('abort')
  }
}

function parseFrame<T>(frame: string): SseEvent<T> | null {
  const trimmed = frame.trim()
  if (!trimmed) return null
  // Skip keep-alive comment lines (`: ping`).
  if (trimmed.startsWith(':')) return null

  let type = 'message'
  const dataLines: string[] = []
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) {
      type = line.slice(6).trim() || 'message'
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^\s/, ''))
    }
  }
  const raw = dataLines.join('\n')
  let data: T | null = null
  if (raw) {
    try {
      data = JSON.parse(raw) as T
    } catch {
      // Non-JSON payloads are surfaced via `raw` so callers can handle them.
    }
  }
  return { type, data, raw }
}
